import { useState } from "react";
import { Trash2 } from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { rupiah, sum, monthKey, EXPENSE_CATS } from "@/lib/format";
import { EntryForm, Section, Stat } from "./shared";

export default function BudgetTab() {
  const [month, setMonth] = useState(monthKey());
  const { items: budgets, add, update, remove } = useCollection("budgets");
  const { items: expenses } = useCollection("expenses", month);
  const mine = budgets.filter((b) => b.month === month);
  const totalB = sum(mine), totalE = sum(expenses);

  const save = async (v) => {
    const existing = mine.find((b) => b.category === v.category);
    const body = { month, category: v.category, amount: Number(v.amount), date: `${month}-01` };
    return existing ? update(existing.id, body) : add(body);
  };

  return (
    <div className="space-y-4" data-testid="duitku-budget">
      <div className="flex items-center gap-3">
        <span className="text-sm font-medium text-pink-600">Bulan</span>
        <input type="month" className="field !w-auto" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="budget-month-input" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <Stat label="Total anggaran" value={rupiah(totalB)} testid="budget-total" />
        <Stat label="Terpakai" value={rupiah(totalE)} tone="text-rose-500" testid="budget-used" />
        <Stat label="Sisa" value={rupiah(totalB - totalE)} tone={totalB - totalE < 0 ? "text-rose-500" : "text-emerald-500"} testid="budget-left" />
      </div>
      <Section title="Atur anggaranku per kategori">
        <EntryForm
          key={month}
          testid="budget-form"
          initial={{ category: "Makan", amount: "" }}
          fields={[
            { name: "category", label: "Kategori", type: "select", options: EXPENSE_CATS.map((c) => [c, c]) },
            { name: "amount", label: "Anggaran (Rp)", type: "number", required: true, min: 0 },
          ]}
          onSubmit={save}
        />
        <div className="mt-5 space-y-4" data-testid="budget-list">
          {mine.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Belum ada anggaran untuk bulan ini.</p>}
          {mine.map((b) => {
            const used = sum(expenses.filter((e) => e.category === b.category));
            const pct = b.amount ? Math.min((used / b.amount) * 100, 100) : 100;
            const over = used > b.amount;
            return (
              <div key={b.id} data-testid="budget-list-item">
                <div className="flex justify-between items-center text-sm">
                  <span className="font-semibold text-gray-700">{b.category}</span>
                  <span className="flex items-center gap-2">
                    <span className={over ? "text-rose-500 font-semibold" : "text-gray-500"}>{rupiah(used)} / {rupiah(b.amount)}</span>
                    <button onClick={() => remove(b.id)} className="text-gray-300 hover:text-rose-500" data-testid="budget-delete" aria-label="Hapus"><Trash2 className="w-4 h-4" /></button>
                  </span>
                </div>
                <div className="h-2.5 bg-pink-50 rounded-full mt-1.5 overflow-hidden">
                  <div className={`h-full rounded-full transition-[width] duration-500 ${over ? "bg-rose-400" : pct > 80 ? "bg-amber-300" : "bg-pink-400"}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}
